import React, { useState, useImperativeHandle, forwardRef } from "react";
import {
  OverlayTrigger,
  Form,
  Tooltip,
  Row,
  Col,
  Button,
  Card
} from "react-bootstrap";
import Select from "react-select";
import { 
  SEARCH_TYPE_OPTIONS,
  SWIM_STATES_FOR_SELECT,
 } from "lookup/SwimConstants";
import { customMultiSelectStyles, customSingleSelectStyles } from "commons/styles/CustomStyles";
 import { isFormEmpty } from "services/UtilityService";

const DedicatedSearchForm = forwardRef(({ onSubmit, isLoading }, ref) => {
  const [selectedSearchType, setSelectedSearchType] = useState(SEARCH_TYPE_OPTIONS[0]);
  const [searchForm, setSearchForm] = useState({
    switchId: '',
    npi: '',
    phone: '',
    fax: '',
    name: '',
    state: '',
  });
  const [disableSearch, setDisableSearch] = useState(true);

  const statesFilterMap = SWIM_STATES_FOR_SELECT
    .filter(state => state.key !== "DEFAULT_FILTERS")
    .map(state => ({ label: state.key, value: state.value }));

  const handleSearchTypeChange = (selected) => {
    setSelectedSearchType(selected);
  };

  const updateSearchForm = (updatedField) => {
    setSearchForm(prevSearchForm => {
      const updatedSearchForm = {
        ...prevSearchForm,
        ...updatedField,
      };
      const shouldDisableSearch = isFormEmpty(updatedSearchForm);
      setDisableSearch(shouldDisableSearch);
      return updatedSearchForm;
    });
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    updateSearchForm({ [name]: value });
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      if ((name === "npi" || name === "phone" || name === "fax") && value.length > 10) {
        return;
      }
      handleFormInputChange(e);
    }
  };

  const handleSwitchStateInputChange = (selected) => {
    updateSearchForm({ state: selected });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(searchForm, selectedSearchType);
  };

  const resetForm = () => {
    setSearchForm({
      switchId: '',
      npi: '',
      phone: '',
      fax: '',
      name: '',
      state: '',
    });
    setDisableSearch(true);
    setSelectedSearchType(SEARCH_TYPE_OPTIONS[0]);
  };

  useImperativeHandle(ref, () => ({
    resetForm,
  }));

  return (
    <Card variant="primary">
      <Card.Header className="text-center">
        Enter Search Criteria
      </Card.Header>
      <Card.Body>
        <Form name="searchForm" onSubmit={handleSubmit}>
          <Row className="mb-3">
            <label htmlFor="inputSwitchId" className="col-sm-2 col-form-label col-form-label-sm">Switch ID</label>
            <Col sm={10}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-switch-id">Digits only</Tooltip>}
              >
                <input type="text" className="form-control form-control-sm" id="inputSwitchId"
                  name="switchId"
                  value={searchForm.switchId}
                  onChange={handleNumberInputChange}
                ></input>
              </OverlayTrigger>
            </Col>
          </Row>
          <Row className="mb-3">
            <label htmlFor="inputSearchType" className="col-sm-2 col-form-label col-form-label-sm">Search For</label>
            <Col sm={10}>
              <Select
              id={"inputSearchType"}
              options={SEARCH_TYPE_OPTIONS}
              value={selectedSearchType}
              onChange={handleSearchTypeChange}
              styles={customMultiSelectStyles}
              >
              </Select>
            </Col>
          </Row>
          <Row className="mb-3">
            <label htmlFor="inputNpi" className="col-sm-2 col-form-label col-form-label-sm">{selectedSearchType.label} NPI #</label>
            <Col sm={10}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-npi">Max 10 digits</Tooltip>}
              >
                <input type="text" className="form-control form-control-sm" id="inputNpi" name="npi"
                  value={searchForm.npi}
                  maxLength="10"
                  onChange={handleNumberInputChange}></input>
              </OverlayTrigger>
            </Col>
          </Row>
          <Row className="mb-3">
            <label htmlFor="inputPhone" className="col-sm-2 col-form-label col-form-label-sm">{selectedSearchType.label} Phone #</label>
            <Col sm={10}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-phone">Max 10 digits</Tooltip>}
              >
                <input type="text" className="form-control form-control-sm" id="inputPhone"
                  name="phone"
                  maxLength="10"
                  value={searchForm.phone}
                  onChange={handleNumberInputChange}></input>
              </OverlayTrigger>
            </Col>
          </Row>
          <Row className="mb-3">
            <label htmlFor="inputFax" className="col-sm-2 col-form-label col-form-label-sm">{selectedSearchType.label} Fax #</label>
            <Col sm={10}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-fax">Max 10 digits</Tooltip>}
              >
                <input type="text" className="form-control form-control-sm" id="inputFax"
                name="fax"
                maxLength="10"
                  value={searchForm.fax}
                  onChange={handleNumberInputChange}></input>
              </OverlayTrigger>
            </Col>
          </Row>
          <Row className="mb-3">
            <label htmlFor="inputName" className="col-sm-2 col-form-label col-form-label-sm">{selectedSearchType.label} Name</label>
            <Col sm={10}>
              <input type="text" className="form-control form-control-sm" id="inputName"
              name="name"
                value={searchForm.name}
                onChange={handleFormInputChange}></input>
            </Col>
          </Row>
          <Row className="mb-3">
            <label htmlFor="inputSwitchState" className="col-sm-2 col-form-label col-form-label-sm">Switch State</label>
            <Col sm={10}>
              <Select
                id={"inputSwitchState"}
                options={statesFilterMap}
                value={searchForm.state}
                onChange={handleSwitchStateInputChange}
                isMulti
                closeMenuOnSelect={false}
                styles={customMultiSelectStyles}
                >
              </Select>
            </Col>
          </Row>
          <Row>
            <Col sm={12} className="d-flex justify-content-end mt-3">
              <Button
                id="submitSearchButton"
                variant="primary"
                type="submit"
                disabled={disableSearch || isLoading}
              >
                Search
              </Button>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
});

export default DedicatedSearchForm;